import { useState, useEffect, useRef, useCallback } from "react";
import {
  parsePageNumbers,
  validatePageNumbersSyntax,
  getFileKind,
} from "@utils/extractionUtils";
import type { Uploaded } from "@utils/extractionUtils";
import type { ExtractionOptions } from "@src/hooks/extraction/useExtraction";

interface UseExtractionFormProps {
  initialOptions?: ExtractionOptions;
  uploadedFile: Uploaded;
}

export const useExtractionForm = ({
  initialOptions,
  uploadedFile,
}: UseExtractionFormProps) => {
  const DEBOUNCE_MS = 3000;

  const [pageNumbersInput, setPageNumbersInput] = useState<string>(
    initialOptions?.pageNumbers || "",
  );
  const [pageNumbers, setPageNumbers] = useState<string>(
    initialOptions?.pageNumbers || "",
  );
  const [instructions, setInstructions] = useState<string>(
    initialOptions?.instructions || "",
  );

  const [pageNumbersError, setPageNumbersError] = useState<string>("");
  const [instructionsError, setInstructionsError] = useState<string>("");

  const [numPages, setNumPages] = useState<number | null>(null);

  const [isDebouncing, setIsDebouncing] = useState<boolean>(false);
  const [countdown, setCountdown] = useState<number>(0);
  const countdownRef = useRef<number | null>(null);
  const debounceRef = useRef<number | null>(null);

  const fileKind = getFileKind(uploadedFile);

  useEffect(() => {
    if (initialOptions) {
      setPageNumbersInput(initialOptions.pageNumbers || "");
      setPageNumbers(initialOptions.pageNumbers || "");
      setInstructions(initialOptions.instructions || "");
    }
  }, [initialOptions]);

  useEffect(() => {
    if (fileKind === "image") {
      setNumPages(1);
      setPageNumbers("1");
      setPageNumbersInput("1");
      setPageNumbersError("");
    }
  }, [fileKind]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (pageNumbersInput === pageNumbers) {
      setIsDebouncing(false);
      return;
    }
    setIsDebouncing(true);

    const totalSeconds = Math.ceil(DEBOUNCE_MS / 1000);
    setCountdown(totalSeconds);
    if (countdownRef.current) clearInterval(countdownRef.current);
    countdownRef.current = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          if (countdownRef.current) {
            clearInterval(countdownRef.current);
            countdownRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    debounceRef.current = window.setTimeout(() => {
      const value = pageNumbersInput;

      if (value && !validatePageNumbersSyntax(value)) {
        setPageNumbersError("Invalid format. Use: 1, 3-5, 6, 73");
        setIsDebouncing(false);
        return;
      }
      if (value && numPages) {
        const parsed = parsePageNumbers(value);
        const outOfRange = parsed.some((p) => p < 1 || p > numPages);
        if (outOfRange) {
          setPageNumbersError(
            `One or more pages are out of range (1–${numPages}).`,
          );
          setIsDebouncing(false);
          return;
        }
      }

      setPageNumbersError("");
      setPageNumbers(value.trim());
      setIsDebouncing(false);
    }, DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
        countdownRef.current = null;
      }
    };
  }, [pageNumbersInput, numPages, pageNumbers]);

  const handlePageNumbersChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setPageNumbersInput(value);
    setPageNumbersError("");
  };

  const handleInstructionsChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = event.target.value;
    setInstructions(value);
    if (instructionsError) setInstructionsError("");
  };

  const commitPageNumbersNow = (): boolean => {
    const value = pageNumbersInput;

    if (value && !validatePageNumbersSyntax(value)) {
      setPageNumbersError("Invalid format. Use: 1, 3-5, 6, 73");
      return false;
    }
    if (value && numPages) {
      const parsed = parsePageNumbers(value);
      const outOfRange = parsed.some((p) => p < 1 || p > numPages);
      if (outOfRange) {
        setPageNumbersError(
          `One or more pages are out of range (1–${numPages}).`,
        );
        return false;
      }
    }

    setPageNumbersError("");
    setPageNumbers(value.trim());
    return true;
  };

  const onDocumentLoadSuccess = useCallback(
    ({ numPages }: { numPages: number }) => {
      setNumPages(numPages);
    },
    [],
  );

  const validateForm = (): boolean => {
    if (!commitPageNumbersNow()) return false;
    return true;
  };

  const getFormData = (): ExtractionOptions => ({
    pageNumbers:
      pageNumbersInput.trim() || (fileKind === "image" ? "1" : undefined),
    instructions: instructions.trim() || undefined,
  });

  const isFormValid =
    !pageNumbersError &&
    !instructionsError;

  return {
    pageNumbersInput,
    pageNumbers,
    instructions,
    numPages,
    pageNumbersError,
    instructionsError,
    isDebouncing,
    countdown,
    fileKind,
    isFormValid,
    handlePageNumbersChange,
    handleInstructionsChange,
    onDocumentLoadSuccess,
    validateForm,
    getFormData,
  };
};
